import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Link2, Trash2, Loader2, UploadCloud } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { PLATFORM_META, formatBytes } from "./AppReleaseCard";

/** Publication des installateurs (upload par morceaux ou URL externe) + liste des versions. */
export function AppReleasePublisher({ onChanged, app = "formation" }) {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [releases, setReleases] = useState([]);
  const [form, setForm] = useState({ platform: "windows", version: "", notes: "", mode: "file", external_url: "" });
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try { const r = await api.get("/admin/app-releases"); setReleases((r.data.releases || []).filter((x) => (x.app || "formation") === app)); } catch { /* silencieux */ }
  }, [api, app]);
  useEffect(() => { load(); }, [load]);

  const done = async (msg) => { toast.success(msg); setForm((f) => ({ ...f, version: "", notes: "", external_url: "" })); setFile(null); if (fileRef.current) fileRef.current.value = ""; await load(); onChanged?.(); };

  const publish = async () => {
    if (!form.version.trim()) return toast.error("Indiquez un numéro de version (ex. 1.0.0)");
    setProgress(0);
    try {
      if (form.mode === "url") {
        await api.post("/admin/app-releases", { app, platform: form.platform, version: form.version, notes: form.notes, external_url: form.external_url });
        return done("Lien de téléchargement publié");
      }
      if (!file) return toast.error("Choisissez un fichier d'installation");
      const init = await api.post("/admin/app-releases/upload/init", { app, platform: form.platform, version: form.version, notes: form.notes, filename: file.name, size: file.size });
      const { upload_id, chunk_size } = init.data;
      const total = Math.ceil(file.size / chunk_size);
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("index", String(i));
        fd.append("chunk", file.slice(i * chunk_size, (i + 1) * chunk_size), "part");
        await api.post(`/admin/app-releases/upload/${upload_id}/chunk`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setProgress(Math.round(((i + 1) / total) * 95));
      }
      await api.post(`/admin/app-releases/upload/${upload_id}/complete`);
      setProgress(100);
      await done(`Installateur ${PLATFORM_META[form.platform].label} v${form.version} publié`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setProgress(null);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Supprimer ${PLATFORM_META[r.platform].label} v${r.version} ?`)) return;
    try { await api.delete(`/admin/app-releases/${r.release_id}`); toast.success("Version supprimée"); await load(); onChanged?.(); } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const inputCls = "w-full bg-[#0b0811] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#f7c935]/60";
  const busy = progress !== null;

  return (
    <div className="bg-[#14101c] border border-white/10 rounded-2xl p-5 mt-6" data-testid="release-publisher">
      <div className="flex items-center gap-2 mb-1"><UploadCloud className="w-5 h-5 text-[#f7c935]" /><h2 className="font-black text-white">Publier une version</h2></div>
      <p className="text-xs text-slate-400 mb-4">Téléversez l'installateur (.exe / .apk / .dmg) généré par le pipeline GitHub Actions, ou collez l'URL d'une release externe. La dernière version publiée par plateforme apparaît ci-dessus pour tous les utilisateurs.</p>

      <div className="grid md:grid-cols-4 gap-3">
        <select value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} className={inputCls} data-testid="release-form-platform" disabled={busy}>
          {Object.entries(PLATFORM_META).map(([k, m]) => <option key={k} value={k}>{m.label} ({m.ext})</option>)}
        </select>
        <input value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} placeholder="Version (ex. 1.0.0)" className={inputCls} data-testid="release-form-version" disabled={busy} />
        <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes (facultatif)" className={`${inputCls} md:col-span-2`} data-testid="release-form-notes" disabled={busy} />
      </div>

      <div className="flex gap-2 mt-3">
        <button type="button" onClick={() => setForm({ ...form, mode: "file" })} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold ${form.mode === "file" ? "bg-[#f7c935] text-[#14101c]" : "bg-white/5 text-slate-300"}`} data-testid="release-mode-file" disabled={busy}><Upload className="w-3.5 h-3.5" /> Fichier</button>
        <button type="button" onClick={() => setForm({ ...form, mode: "url" })} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold ${form.mode === "url" ? "bg-[#f7c935] text-[#14101c]" : "bg-white/5 text-slate-300"}`} data-testid="release-mode-url" disabled={busy}><Link2 className="w-3.5 h-3.5" /> URL externe</button>
      </div>

      <div className="mt-3 flex flex-col md:flex-row gap-3 md:items-center">
        {form.mode === "file" ? (
          <input ref={fileRef} type="file" accept=".exe,.msi,.apk,.dmg,.pkg,.zip" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-xs text-slate-300 file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-white/10 file:text-white file:text-xs file:font-bold" data-testid="release-form-file" disabled={busy} />
        ) : (
          <input value={form.external_url} onChange={(e) => setForm({ ...form, external_url: e.target.value })} placeholder="https://github.com/…/releases/download/…/Blitzz-Formation-Setup.exe" className={`${inputCls} flex-1`} data-testid="release-form-url" disabled={busy} />
        )}
        <Button onClick={publish} disabled={busy} className="bg-[#f7c935] text-[#14101c] font-bold shrink-0" data-testid="release-publish-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UploadCloud className="w-4 h-4 mr-2" />} {busy ? `Envoi… ${progress}%` : "Publier"}
        </Button>
      </div>
      {file && form.mode === "file" && <div className="text-[11px] text-slate-500 mt-1" data-testid="release-form-file-info">{file.name} — {formatBytes(file.size)}</div>}
      {busy && <div className="mt-2 h-1.5 rounded-full bg-white/5 overflow-hidden"><div className="h-full bg-[#f7c935] transition-[width]" style={{ width: `${progress}%` }} data-testid="release-upload-progress" /></div>}

      <ReleaseList releases={releases} onDelete={remove} />
    </div>
  );
}

function ReleaseList({ releases, onDelete }) {
  if (!releases.length) return <div className="text-xs text-slate-500 italic mt-4" data-testid="release-list-empty">Aucune version publiée.</div>;
  return (
    <div className="mt-4 divide-y divide-white/5" data-testid="release-list">
      {releases.map((r) => {
        const m = PLATFORM_META[r.platform];
        return (
          <div key={r.release_id} className="flex items-center gap-3 py-2 text-xs" data-testid={`release-row-${r.release_id}`}>
            <m.icon className="w-4 h-4 shrink-0" style={{ color: m.color }} />
            <span className="font-bold text-white w-16">{m.label}</span>
            <span className="text-[#f7c935] font-bold">v{r.version}</span>
            <span className="text-slate-400 truncate flex-1">{r.external_url ? r.external_url : `${r.filename} · ${formatBytes(r.size)}`}</span>
            <span className="text-slate-500 hidden md:inline">{new Date(r.created_at).toLocaleDateString("fr-FR")} · {r.downloads || 0} téléch.</span>
            <button type="button" onClick={() => onDelete(r)} className="p-1.5 rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-500/10" title="Supprimer" data-testid={`release-delete-${r.release_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        );
      })}
    </div>
  );
}
